import { z } from 'zod';
import { accountRequestSchema } from '../../account/api/account-types';
import { bucketRequestSchema } from '../../bucket/api/bucket-types';
import { categoryRequestSchema } from '../../category/api/category-types';
import { transactionRequestSchema } from '../../transaction/api/transaction-types';
import { viewRequestSchema } from '../../view/api/view-types';
import { budgetCurrencySchema } from './budget-currency';
import { budgetLocaleSchema } from './budget-locale';
export var TableView;
(function (TableView) {
    TableView["ALL"] = "ALL";
    TableView["STANDARD"] = "STANDARD";
    TableView["BASIC"] = "BASIC";
    TableView["MINIMAL"] = "MINIMAL";
})(TableView || (TableView = {}));
// Expense projection types
export var ExpenseProjectionType;
(function (ExpenseProjectionType) {
    ExpenseProjectionType["NONE"] = "NONE";
    ExpenseProjectionType["AUTO_ASSIGN"] = "AUTO_ASSIGN";
    ExpenseProjectionType["UPCOMING_TRANSACTIONS"] = "UPCOMING_TRANSACTIONS";
    ExpenseProjectionType["THREE_MONTH_AVERAGE"] = "THREE_MONTH_AVERAGE";
    ExpenseProjectionType["SIX_MONTH_AVERAGE"] = "SIX_MONTH_AVERAGE";
    ExpenseProjectionType["TWELVE_MONTH_AVERAGE"] = "TWELVE_MONTH_AVERAGE";
    ExpenseProjectionType["EXCLUDED"] = "EXCLUDED";
})(ExpenseProjectionType || (ExpenseProjectionType = {}));
// Create selection options for UI dropdowns
export const expenseProjectionTypeSelectOptions = [
    { value: ExpenseProjectionType.NONE, label: 'No Projections' },
    { value: ExpenseProjectionType.AUTO_ASSIGN, label: 'Auto-Assign Amount' },
    { value: ExpenseProjectionType.UPCOMING_TRANSACTIONS, label: 'Upcoming Transactions' },
    { value: ExpenseProjectionType.THREE_MONTH_AVERAGE, label: '3-Month Average' },
    { value: ExpenseProjectionType.SIX_MONTH_AVERAGE, label: '6-Month Average' },
    { value: ExpenseProjectionType.TWELVE_MONTH_AVERAGE, label: '12-Month Average' },
    { value: ExpenseProjectionType.EXCLUDED, label: 'Excluded (Savings)' },
];
// Date Format
export var BudgetFormatDate;
(function (BudgetFormatDate) {
    BudgetFormatDate["YYYY_MM_DD_SLASH"] = "YYYY_MM_DD_SLASH";
    BudgetFormatDate["YYYY_MM_DD_DASH"] = "YYYY_MM_DD_DASH";
    BudgetFormatDate["DD_MM_YYYY_DASH"] = "DD_MM_YYYY_DASH";
    BudgetFormatDate["DD_MM_YYYY_SLASH"] = "DD_MM_YYYY_SLASH";
    BudgetFormatDate["DD_MM_YYYY_DOT"] = "DD_MM_YYYY_DOT";
    BudgetFormatDate["MM_DD_YYYY_SLASH"] = "MM_DD_YYYY_SLASH";
    BudgetFormatDate["YYYY_MM_DD_DOT"] = "YYYY_MM_DD_DOT";
})(BudgetFormatDate || (BudgetFormatDate = {}));
export const budgetFormatDateSchema = z.enum([
    BudgetFormatDate.YYYY_MM_DD_SLASH,
    BudgetFormatDate.YYYY_MM_DD_DASH,
    BudgetFormatDate.DD_MM_YYYY_DASH,
    BudgetFormatDate.DD_MM_YYYY_SLASH,
    BudgetFormatDate.DD_MM_YYYY_DOT,
    BudgetFormatDate.MM_DD_YYYY_SLASH,
    BudgetFormatDate.YYYY_MM_DD_DOT,
]);
// Number Format
export var BudgetFormatNumber;
(function (BudgetFormatNumber) {
    BudgetFormatNumber["COMMA_DOT"] = "COMMA_DOT";
    BudgetFormatNumber["DOT_COMMA"] = "DOT_COMMA";
    BudgetFormatNumber["COMMA_DOT_THREE"] = "COMMA_DOT_THREE";
    BudgetFormatNumber["SPACE_DOT"] = "SPACE_DOT";
    BudgetFormatNumber["APOSTROPHE_DOT"] = "APOSTROPHE_DOT";
    BudgetFormatNumber["DOT_ONLY"] = "DOT_ONLY";
    BudgetFormatNumber["COMMA_ONLY"] = "COMMA_ONLY";
    BudgetFormatNumber["SPACE_DASH"] = "SPACE_DASH";
    BudgetFormatNumber["SPACE_COMMA"] = "SPACE_COMMA";
    BudgetFormatNumber["COMMA_SLASH"] = "COMMA_SLASH";
    BudgetFormatNumber["SPACE_ONLY"] = "SPACE_ONLY";
    BudgetFormatNumber["INDIAN_COMMA"] = "INDIAN_COMMA";
})(BudgetFormatNumber || (BudgetFormatNumber = {}));
export const budgetFormatNumberSchema = z.enum([
    BudgetFormatNumber.COMMA_DOT,
    BudgetFormatNumber.DOT_COMMA,
    BudgetFormatNumber.COMMA_DOT_THREE,
    BudgetFormatNumber.SPACE_DOT,
    BudgetFormatNumber.APOSTROPHE_DOT,
    BudgetFormatNumber.DOT_ONLY,
    BudgetFormatNumber.COMMA_ONLY,
    BudgetFormatNumber.SPACE_DASH,
    BudgetFormatNumber.SPACE_COMMA,
    BudgetFormatNumber.COMMA_SLASH,
    BudgetFormatNumber.SPACE_ONLY,
    BudgetFormatNumber.INDIAN_COMMA,
]);
// budget settings
export const budgetSettingsSchema = z.object({
    tableView: z.nativeEnum(TableView),
    collapsedCategoryIds: z.array(z.string()),
    bucketProjections: z.record(z.string(), z.nativeEnum(ExpenseProjectionType)).optional().default({}),
});
// budget request
export const budgetRequestSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    notes: z.string().optional(),
    currency: budgetCurrencySchema,
    locale: budgetLocaleSchema,
    settings: budgetSettingsSchema,
    position: z.number(),
});
// budget request add
export const budgetRequestAddSchema = budgetRequestSchema.extend({
    accounts: accountRequestSchema.array(),
    transactions: transactionRequestSchema.array(),
    categories: categoryRequestSchema.array(),
    buckets: bucketRequestSchema.array(),
    views: viewRequestSchema.array(),
});
// budget
export const budgetSchema = budgetRequestSchema.extend({});
// budget import
export const budgetRequestImportSchema = budgetRequestAddSchema.extend({
    file: z.instanceof(File),
    dateFormat: budgetFormatDateSchema,
    numberFormat: budgetFormatNumberSchema,
});
// budget export
export const budgetRequestExportSchema = z.object({
    id: z.string().uuid(),
});
// budget delete
export const budgetDeleteSchema = z.object({
    id: z.string().uuid(),
});
// budget sort
export const budgetRequestSortSchema = z.object({
    ids: z.array(z.string().uuid()),
});
export const budgetFormatDateSelectOptions = [
    { value: BudgetFormatDate.YYYY_MM_DD_SLASH, label: '2025/12/30' },
    { value: BudgetFormatDate.YYYY_MM_DD_DASH, label: '2025-12-30' },
    { value: BudgetFormatDate.DD_MM_YYYY_DASH, label: '30-12-2025' },
    { value: BudgetFormatDate.DD_MM_YYYY_SLASH, label: '30/12/2025' },
    { value: BudgetFormatDate.DD_MM_YYYY_DOT, label: '30.12.2025' },
    { value: BudgetFormatDate.MM_DD_YYYY_SLASH, label: '12/30/2025' },
    { value: BudgetFormatDate.YYYY_MM_DD_DOT, label: '2025.12.30' },
];
export const budgetFormatNumberSelectOptions = [
    { value: BudgetFormatNumber.COMMA_DOT, label: '123,456.78' },
    { value: BudgetFormatNumber.DOT_COMMA, label: '123.456,78' },
    { value: BudgetFormatNumber.COMMA_DOT_THREE, label: '123,456.789' },
    { value: BudgetFormatNumber.SPACE_DOT, label: '123 456.78' },
    { value: BudgetFormatNumber.APOSTROPHE_DOT, label: "123'456.78" },
    { value: BudgetFormatNumber.DOT_ONLY, label: '123.456' },
    { value: BudgetFormatNumber.COMMA_ONLY, label: '123,456' },
    { value: BudgetFormatNumber.SPACE_DASH, label: '123 456-78' },
    { value: BudgetFormatNumber.SPACE_COMMA, label: '123 456,78' },
    { value: BudgetFormatNumber.COMMA_SLASH, label: '123,456/78' },
    { value: BudgetFormatNumber.SPACE_ONLY, label: '123 456' },
    { value: BudgetFormatNumber.INDIAN_COMMA, label: '1,23,456.78' },
];
